import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { DEFAULT_LANGUAGE, LANGUAGES, LANGUAGE_STORAGE_KEY } from "../const/languages";
import en from "./locales/en.json";
import { isChunkLoadError, reloadOnceForChunkError } from "../utils/chunkLoadRecovery";

const SUPPORTED = LANGUAGES.map(({ code }) => code);

// Only English ships in the main bundle; other locales are webpack chunks (~35–50 KB each).
const LOCALE_LOADERS = {
  es: () => import(/* webpackChunkName: "locale-es" */ "./locales/es.json"),
  "pt-BR": () => import(/* webpackChunkName: "locale-pt-BR" */ "./locales/pt-BR.json"),
  de: () => import(/* webpackChunkName: "locale-de" */ "./locales/de.json"),
  fr: () => import(/* webpackChunkName: "locale-fr" */ "./locales/fr.json"),
  it: () => import(/* webpackChunkName: "locale-it" */ "./locales/it.json"),
  nl: () => import(/* webpackChunkName: "locale-nl" */ "./locales/nl.json"),
  pl: () => import(/* webpackChunkName: "locale-pl" */ "./locales/pl.json"),
  uk: () => import(/* webpackChunkName: "locale-uk" */ "./locales/uk.json"),
  ru: () => import(/* webpackChunkName: "locale-ru" */ "./locales/ru.json"),
};

function readStoredLanguage() {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function isSupportedLanguage(code) {
  return SUPPORTED.includes(code);
}

// English unless the visitor picked a language on this device; the browser language is deliberately ignored.
function initialLanguage() {
  const stored = readStoredLanguage();
  return isSupportedLanguage(stored) ? stored : DEFAULT_LANGUAGE;
}

async function ensureLocale(code) {
  if (code === DEFAULT_LANGUAGE || i18n.hasResourceBundle(code, "translation")) return;
  const loader = LOCALE_LOADERS[code];
  if (!loader) return;
  const mod = await loader();
  i18n.addResourceBundle(code, "translation", mod.default ?? mod, true, true);
}

export async function changeLanguage(code) {
  const next = isSupportedLanguage(code) ? code : DEFAULT_LANGUAGE;
  try {
    await ensureLocale(next);
  } catch (error) {
    // The locale chunk from the previous deploy is gone; reload picks up the new hash.
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
      return i18n;
    }
    throw error;
  }
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
  } catch {
    // Storage can be blocked (private mode, Telegram webview); the choice then lasts for the session.
  }
  return i18n.changeLanguage(next);
}

i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") document.documentElement.lang = lng;
});

i18n.use(initReactI18next).init({
  resources: { [DEFAULT_LANGUAGE]: { translation: en } },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED,
  load: "currentOnly",
  initImmediate: false,
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: false,
  interpolation: {
    escapeValue: false,
    prefix: "{",
    suffix: "}",
    // Keep unresolved placeholders verbatim, matching the pre-i18next t() behaviour.
    missingInterpolationHandler: (_text, match) => match[0],
  },
});

/** Resolves once the stored device language (if any) is loaded. English resolves immediately. */
export const ready = (async () => {
  const stored = initialLanguage();
  if (stored === DEFAULT_LANGUAGE) {
    if (typeof document !== "undefined") document.documentElement.lang = stored;
    return;
  }
  await changeLanguage(stored);
})();

export default i18n;
